import { type Common } from '../common.js';
import {
  type HumanTask,
  type HumanTaskAssetPayload,
  type HumanTaskAssetResponse,
  type HumanTaskClaimNextQuery,
  type HumanTaskFindAllQuery,
  type HumanTaskSearchResult,
  type HumanTaskSubtaskStepPayload,
} from '../models/flow/humanTask/humanTask.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/human-tasks';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(body?: HumanTaskFindAllQuery): Promise<HumanTaskSearchResult[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/search`);
    return await this.common.postJSON(url, body ?? {});
  }

  async findAllTags(): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/tags`);
    return await this.common.getJSON(url);
  }

  async findOneById(humanTaskId: string): Promise<HumanTask> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}`);
    return await this.common.getJSON(url);
  }

  async getTaskTags(humanTaskId: string): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/tags`);
    return await this.common.getJSON(url);
  }

  async updateTaskTags(options: { humanTaskId: string; tags: string[] }): Promise<string[]> {
    const { humanTaskId, tags } = options;
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/tags`);
    return await this.common.patchJSON(url, { tags });
  }

  async findOneByExternalId(humanTaskExternalId: string): Promise<HumanTask> {
    const url = this.common.constructPath(`${this.baseEndpoint}/external/${humanTaskExternalId}`);
    return await this.common.getJSON(url);
  }

  async claimTask(humanTaskId: string): Promise<{ success: true; humanTask: HumanTask }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/claim`);
    return await this.common.postJSON(url, {});
  }

  async claimNextTask(
    humanTaskId: string,
    body?: HumanTaskClaimNextQuery
  ): Promise<{ success: true; humanTask: HumanTask }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/claim-next`);
    return await this.common.postJSON(url, body ?? {});
  }

  async unclaimTask(humanTaskId: string): Promise<{ success: true; humanTask: HumanTask }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/unclaim`);
    return await this.common.postJSON(url, {});
  }

  async expireTask(humanTaskId: string): Promise<{ success: true; humanTask: HumanTask }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/expire`);
    return await this.common.postJSON(url, {});
  }

  async uploadAsset(props: {
    humanTaskId: string;
    payload: HumanTaskAssetPayload;
  }): Promise<HumanTaskAssetResponse> {
    const { humanTaskId, payload } = props;
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/uploadAsset`);
    return await this.common.postJSON(url, payload);
  }

  async completeSubtaskStep(props: {
    humanTaskId: string;
    subtaskId: string;
    stepId: string;
    payload: HumanTaskSubtaskStepPayload;
  }): Promise<{ success: true; humanTask: HumanTask; description: unknown }> {
    const { humanTaskId, subtaskId, stepId, payload } = props;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${humanTaskId}/subtasks/${subtaskId}/steps/${stepId}/complete`
    );
    return await this.common.postJSON(url, payload);
  }

  async completeTask(
    humanTaskId: string
  ): Promise<{ success: true; humanTask: HumanTask; description: unknown }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${humanTaskId}/complete`);
    return await this.common.postJSON(url, {});
  }
}
